// =============================================================================
// features/sales/agenda/AppointmentList.tsx — Corpo da lista: resolve o
// estado (loading/erro/vazio/sucesso), agrupa por dia no modo calendário e
// pagina (doc 26 §6.1).
// =============================================================================

import type { Appointment } from '@elemento/shared-schemas';
import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useAppointmentLeadSummaries } from '../../../hooks/sales/useAppointmentLeadSummaries';

import type { AgendaViewMode } from './AgendaHeader';
import { AgendaEmptyState, AgendaErrorState, AppointmentListSkeleton } from './AgendaStates';
import { AppointmentCard } from './AppointmentCard';
import { groupByDay } from './filters';
import { resolveAgendaContentState } from './viewState';

export interface AppointmentListProps {
  items: Appointment[];
  now: Date;
  isLoading: boolean;
  isError: boolean;
  hasExtraFilters: boolean;
  viewMode: AgendaViewMode;
  onRetry: () => void;
  onClearFilters: () => void;
  agentNameById: Map<string, string>;
  showAgentName: boolean;
  // Paginação server-side (offset/limit/total vêm da API). `items` já passou
  // pelo filtro de campanha client-side (ver filters.ts) — pode ter menos
  // linhas que `limit` mesmo sem ser a última página; ver tratamento do
  // estado `empty-filtered` abaixo.
  offset: number;
  limit: number;
  total: number;
  onOffsetChange: (offset: number) => void;
}

export function AppointmentList({
  items,
  now,
  isLoading,
  isError,
  hasExtraFilters,
  viewMode,
  onRetry,
  onClearFilters,
  agentNameById,
  showAgentName,
  offset,
  limit,
  total,
  onOffsetChange,
}: AppointmentListProps): React.JSX.Element {
  const leadIds = items.map((a) => a.leadId);
  const { summaries, failedIds } = useAppointmentLeadSummaries(leadIds);

  const state = resolveAgendaContentState({
    isLoading,
    isError,
    itemCount: items.length,
    hasExtraFilters,
  });

  if (state === 'loading') return <AppointmentListSkeleton />;
  if (state === 'error') return <AgendaErrorState onRetry={onRetry} />;
  if (state === 'empty-no-agenda')
    return <AgendaEmptyState filtered={false} onClearFilters={onClearFilters} />;
  if (state === 'empty-filtered') {
    // `total` é o total do SERVIDOR para a janela/canal/responsável/situação
    // (todos já filtram no backend). O filtro de campanha continua
    // client-side (ver nota em filters.ts) e roda só sobre a página atual —
    // então "zero itens nesta página" não significa "zero itens no total":
    // outra página do servidor pode ter compromissos que batem com o
    // filtro. Mantemos a paginação visível e navegável nesse caso, em vez de
    // esconder o usuário atrás de um beco sem saída (achado de revisão
    // F33-S05).
    const hasOtherPages = total > limit;
    return (
      <div className="flex flex-col gap-6">
        <AgendaEmptyState filtered hasOtherPages={hasOtherPages} onClearFilters={onClearFilters} />
        <ListPagination
          offset={offset}
          limit={limit}
          total={total}
          onOffsetChange={onOffsetChange}
        />
      </div>
    );
  }

  const renderCard = (appointment: Appointment): React.JSX.Element => (
    <AppointmentCard
      key={appointment.id}
      appointment={appointment}
      now={now}
      leadSummary={summaries.get(appointment.leadId)}
      leadResolutionFailed={failedIds.has(appointment.leadId)}
      agentName={
        showAgentName ? (agentNameById.get(appointment.assignedAgentId) ?? 'Agente') : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-6">
      {viewMode === 'lista' ? (
        <div className="flex flex-col gap-3">{items.map(renderCard)}</div>
      ) : (
        <div className="flex flex-col gap-6">
          {groupByDay(items).map((group) => (
            <div key={group.dayKey} className="flex flex-col gap-3">
              <h3 className="font-sans text-xs font-bold uppercase tracking-[0.1em] text-ink-3">
                {group.label}
              </h3>
              <div className="flex flex-col gap-3">{group.items.map(renderCard)}</div>
            </div>
          ))}
        </div>
      )}

      <ListPagination offset={offset} limit={limit} total={total} onOffsetChange={onOffsetChange} />
    </div>
  );
}

function ListPagination({
  offset,
  limit,
  total,
  onOffsetChange,
}: {
  offset: number;
  limit: number;
  total: number;
  onOffsetChange: (offset: number) => void;
}): React.JSX.Element | null {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <nav className="flex items-center justify-center gap-3" aria-label="Paginação da agenda">
      <Button
        variant="ghost"
        size="sm"
        disabled={offset <= 0}
        onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        aria-label="Página anterior"
      >
        Anterior
      </Button>
      <span className="font-sans text-sm text-ink-3">
        {currentPage} / {totalPages}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={currentPage >= totalPages}
        onClick={() => onOffsetChange(offset + limit)}
        aria-label="Próxima página"
      >
        Próxima
      </Button>
    </nav>
  );
}
